// src/pages/CRM/Finance/InvoiceList.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, Tabs, Tab, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  LinearProgress, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  FileUpload as ImportIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { hasPermission } from '../../../../utils/permissionUtils';
import axiosInstance from '../../../../utils/axiosInstance';
import { InvoiceStatus, InvoiceStatusLabels, InvoiceStatusColors } from '../Core/crmEnums';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const STATUS_TABS = [
  { label: 'All', value: '' },
  { label: 'Draft', value: InvoiceStatus.DRAFT },
  { label: 'Sent', value: InvoiceStatus.SENT },
  { label: 'Partially Paid', value: InvoiceStatus.PARTIALLY_PAID },
  { label: 'Paid', value: InvoiceStatus.PAID },
  { label: 'Overdue', value: InvoiceStatus.OVERDUE },
  { label: 'Cancelled', value: InvoiceStatus.CANCELLED },
];

const InvoiceList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, invoice: null });
  const [actionLoading, setActionLoading] = useState(false);

  // Import state
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    document.title = 'Invoices | KlonIT CRM';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadInvoices();
    }
  }, [canView, chatbotId, page, rowsPerPage, statusFilter]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) setLoading(false);
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        ...(chatbotId && { chatbot_id: chatbotId }),
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search }),
      };
      const response = await axiosInstance.get('/api/crm/invoices', { params });
      if (response.data.success) {
        setInvoices(response.data.data?.invoices || []);
        setTotalCount(response.data.data?.total || 0);
      } else {
        setError(response.data.message || 'Failed to load invoices');
      }
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [chatbotId, page, rowsPerPage, statusFilter, search]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      loadInvoices();
    }
  };

  const handleSendInvoice = async (invoice) => {
    setActionLoading(true);
    try {
      const response = await axiosInstance.post(`/api/crm/invoices/${invoice.id}/send`);
      if (response.data.success) {
        enqueueSnackbar('Invoice sent successfully', { variant: 'success' });
        loadInvoices();
      } else {
        enqueueSnackbar(response.data.message || 'Failed to send invoice', { variant: 'error' });
      }
    } catch (err) {
      console.error('Error sending invoice:', err);
      enqueueSnackbar('Failed to send invoice', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteDialog.invoice) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.delete(`/api/crm/invoices/${deleteDialog.invoice.id}`);
      if (response.data.success) {
        enqueueSnackbar('Invoice deleted successfully', { variant: 'success' });
        setDeleteDialog({ open: false, invoice: null });
        loadInvoices();
      } else {
        enqueueSnackbar(response.data.message || 'Failed to delete invoice', { variant: 'error' });
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
      enqueueSnackbar('Failed to delete invoice', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenImport = () => {
    setImportFile(null);
    setImportResult(null);
    setImportDialog(true);
  };

  const handleCloseImport = () => {
    setImportDialog(false);
    setImportFile(null);
    setImportResult(null);
    if (importResult?.success_count > 0) {
      loadInvoices();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validTypes.includes(ext)) {
        enqueueSnackbar('Please select a CSV or Excel file (.csv, .xlsx, .xls)', { variant: 'error' });
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await axiosInstance.post('/api/crm/salestransaction/invoices/import', formData, {
        params: { ...(chatbotId && { chatbot_id: chatbotId }) },
      });
      if (response.data.success) {
        setImportResult(response.data.data);
        if (response.data.data.success_count > 0) {
          enqueueSnackbar(`Successfully imported ${response.data.data.success_count} invoice(s)`, { variant: 'success' });
        }
      } else {
        enqueueSnackbar(response.data.message || 'Import failed', { variant: 'error' });
      }
    } catch (err) {
      console.error('Error importing invoices:', err);
      enqueueSnackbar(err.response?.data?.message || 'Failed to import invoices', { variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async (format = 'csv') => {
    try {
      const response = await axiosInstance.get('/api/crm/salestransaction/invoices/template', {
        params: { format },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_import_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      enqueueSnackbar('Failed to download template', { variant: 'error' });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  if (!canView && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view invoices.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, fontFamily }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/salestransaction`)}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily }}>
            Invoices
          </Typography>
        </Stack>
        {canModify && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={handleOpenImport}
              sx={{ fontFamily, textTransform: 'none', borderColor: '#3b82f6', color: '#3b82f6', '&:hover': { borderColor: '#2563eb', bgcolor: '#eff6ff' } }}
            >
              Import
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/salestransaction/invoices/new`)}
              sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
            >
              New Invoice
            </Button>
          </Stack>
        )}
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by invoice number or contact name..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9ca3af' }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 400, fontFamily, '& .MuiInputBase-root': { fontFamily } }}
        />
      </Box>

      {/* Status Tabs */}
      <Tabs
        value={statusFilter}
        onChange={(e, val) => { setStatusFilter(val); setPage(0); }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, '& .MuiTab-root': { fontFamily, textTransform: 'none' } }}
      >
        {STATUS_TABS.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={loadInvoices}>Retry</Button>
        }>{error}</Alert>
      )}

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Invoice #</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Contact</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Due Date</TableCell>
                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Total</TableCell>
                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Paid</TableCell>
                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Balance</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontFamily, fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, fontFamily, color: '#9ca3af' }}>
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell sx={{ fontFamily, fontWeight: 500 }}>{invoice.invoice_number}</TableCell>
                    <TableCell sx={{ fontFamily }}>{invoice.contact ? `${invoice.contact.first_name || ''} ${invoice.contact.last_name || ''}`.trim() : '-'}</TableCell>
                    <TableCell sx={{ fontFamily, color: '#6b7280' }}>{formatDate(invoice.invoice_date)}</TableCell>
                    <TableCell sx={{ fontFamily, color: '#6b7280' }}>{formatDate(invoice.due_date)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily }}>{formatCurrency(invoice.total_amount)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily, color: '#22c55e' }}>
                      {formatCurrency(invoice.amount_paid)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>
                      {formatCurrency(invoice.balance_due)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={InvoiceStatusLabels[invoice.status] || invoice.status}
                        color={InvoiceStatusColors[invoice.status] || 'default'}
                        size="small"
                        sx={{ fontFamily }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0} justifyContent="center">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/chatbot/${chatbotId}/crm/salestransaction/invoices/${invoice.id}`)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canModify && invoice.status === InvoiceStatus.DRAFT && (
                          <Tooltip title="Send">
                            <IconButton
                              size="small"
                              onClick={() => handleSendInvoice(invoice)}
                              disabled={actionLoading}
                            >
                              <SendIcon fontSize="small" sx={{ color: '#3b82f6' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canModify && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog({ open: true, invoice })}
                              disabled={actionLoading}
                            >
                              <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, invoice: null })}>
        <DialogTitle sx={{ fontFamily }}>Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily }}>
            Are you sure you want to delete invoice <strong>{deleteDialog.invoice?.invoice_number}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, invoice: null })}
            sx={{ fontFamily, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteInvoice}
            color="error"
            variant="contained"
            disabled={actionLoading}
            sx={{ fontFamily, textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Invoices Dialog */}
      <Dialog
        open={importDialog}
        onClose={!importing ? handleCloseImport : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Import Invoices
          {!importing && (
            <IconButton size="small" onClick={handleCloseImport}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          {!importResult ? (
            <Box>
              <Typography variant="body2" sx={{ fontFamily, color: '#6b7280', mb: 2 }}>
                Upload a CSV or Excel file to bulk import invoices. Rows with the same contact email
                and dates are grouped into a single invoice with multiple line items.
              </Typography>

              {/* Download Template */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f9fafb', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle2" sx={{ fontFamily, fontWeight: 600, mb: 1 }}>
                  Download Template
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: '#6b7280', mb: 1.5 }}>
                  Start with our template to ensure your data is formatted correctly.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadTemplate('csv')}
                    sx={{ fontFamily, textTransform: 'none' }}
                  >
                    CSV Template
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadTemplate('xlsx')}
                    sx={{ fontFamily, textTransform: 'none' }}
                  >
                    Excel Template
                  </Button>
                </Stack>
              </Paper>

              {/* File Upload Area */}
              <Box
                sx={{
                  border: '2px dashed #d1d5db',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  bgcolor: importFile ? '#f0fdf4' : '#fafafa',
                  borderColor: importFile ? '#22c55e' : '#d1d5db',
                  '&:hover': { borderColor: '#3b82f6', bgcolor: '#eff6ff' },
                }}
                onClick={() => document.getElementById('invoice-import-file').click()}
              >
                <input
                  id="invoice-import-file"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                {importFile ? (
                  <>
                    <CheckCircleIcon sx={{ fontSize: 40, color: '#22c55e', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontFamily, fontWeight: 600 }}>
                      {importFile.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#6b7280' }}>
                      {(importFile.size / 1024).toFixed(1)} KB — Click to change file
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: '#9ca3af', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontFamily, fontWeight: 500 }}>
                      Click to select a file
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#9ca3af' }}>
                      Supports CSV, XLSX, XLS
                    </Typography>
                  </>
                )}
              </Box>

              {importing && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ fontFamily, color: '#6b7280', mt: 1, textAlign: 'center' }}>
                    Importing invoices...
                  </Typography>
                </Box>
              )}

              {/* Required Columns Info */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ fontFamily, color: '#9ca3af' }}>
                  Required columns: contact_email, item_description, quantity, unit_price
                </Typography>
                <br />
                <Typography variant="caption" sx={{ fontFamily, color: '#9ca3af' }}>
                  Optional: invoice_date, due_date, status, tax_rate, discount_amount, notes, terms
                </Typography>
              </Box>
            </Box>
          ) : (
            // Import Results
            <Box>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                {importResult.success_count > 0 ? (
                  <CheckCircleIcon sx={{ fontSize: 48, color: '#22c55e', mb: 1 }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 48, color: '#ef4444', mb: 1 }} />
                )}
                <Typography variant="h6" sx={{ fontFamily, fontWeight: 600 }}>
                  Import Complete
                </Typography>
              </Box>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }} justifyContent="center">
                <Paper sx={{ p: 2, textAlign: 'center', flex: 1, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                  <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#22c55e' }}>
                    {importResult.success_count}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily, color: '#6b7280' }}>
                    Imported
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center', flex: 1, bgcolor: importResult.error_count > 0 ? '#fef2f2' : '#f9fafb', borderRadius: 2 }}>
                  <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: importResult.error_count > 0 ? '#ef4444' : '#9ca3af' }}>
                    {importResult.error_count}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily, color: '#6b7280' }}>
                    Errors
                  </Typography>
                </Paper>
              </Stack>

              {importResult.errors && importResult.errors.length > 0 && (
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="subtitle2" sx={{ fontFamily, fontWeight: 600, mb: 1, color: '#ef4444' }}>
                    Errors:
                  </Typography>
                  <List dense disablePadding>
                    {importResult.errors.map((err, idx) => (
                      <ListItem key={idx} disableGutters sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <ErrorIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={err}
                          primaryTypographyProps={{ variant: 'body2', fontFamily, color: '#6b7280' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!importResult ? (
            <>
              <Button
                onClick={handleCloseImport}
                disabled={importing}
                sx={{ fontFamily, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                variant="contained"
                disabled={!importFile || importing}
                startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <ImportIcon />}
                sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleCloseImport}
              variant="contained"
              sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;
